// MintGate — client-side pacing for POST /api/masters/upload-token.
//
// The backend rate-limits upload-token mints to 15 per rolling
// 300s per creator (middleware/rateLimit.js — a sliding window).
// This gate mirrors that exact algorithm in page memory so the
// upload worker pool paces itself and, in the common case, never
// trips a 429 in the first place.
//
// LIMITATION — per-tab, not per-creator. The gate lives in page
// memory, so two browser tabs of the same creator run two
// independent gates; their combined mint load can exceed 15/300s
// and the server WILL 429 the excess. That is handled correctly by
// the caller's 429 backoff (see penalize() below) — it just means
// a multi-tab creator sees longer, burstier wall-clock. Cross-tab
// coordination (BroadcastChannel / localStorage) is out of scope.

const WINDOW_MS = 5 * 60 * 1000;   // mirrors rateLimit.js WINDOW_MS
const MAX_HITS  = 15;              // mirrors rateLimit.js MAX_HITS

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Returns a gate with acquire() / penalize(ms).
export function createMintGate() {
  const hits = [];        // mint timestamps (ms) within the window
  let blockedUntil = 0;   // server-directed hold, set by penalize()

  // Resolves when a mint is permitted, recording it on resolve — so
  // the caller MUST mint immediately after acquire() resolves.
  // onWait(untilTs), if given, fires whenever the gate has to wait
  // (so the UI can show a countdown).
  async function acquire(onWait) {
    // Loop: a wait may be followed by another — a sibling worker
    // can take the freed slot first.
    for (;;) {
      const now = Date.now();
      while (hits.length && hits[0] <= now - WINDOW_MS) hits.shift();

      const windowWait = hits.length >= MAX_HITS
        ? hits[0] + WINDOW_MS - now
        : 0;
      const wait = Math.max(windowWait, blockedUntil - now);

      if (wait <= 0) {
        hits.push(now);
        return;
      }
      if (onWait) onWait(now + wait);
      await sleep(wait);
    }
  }

  // Called by a worker that got a 429 anyway (e.g. another tab is
  // also minting). Holds every worker on this gate until now+ms so
  // they don't each independently re-trip the limiter.
  function penalize(ms) {
    blockedUntil = Math.max(blockedUntil, Date.now() + ms);
  }

  return { acquire, penalize };
}
